import type { EconomicsCell, UnitEconomics, UnitEconomicsByCountry } from '@growth/client';
import { Link, useParams } from 'react-router';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { AssumptionsNote, AttributionToggle, BasisToggle, fmtRoas, TargetBadge, useMoneyParams } from '../components/money.tsx';
import { Card, DataTable, Empty, ErrorBox } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtInt, fmtMoney, fmtNum, fmtPct } from '../format.ts';
import { useApi } from '../session.tsx';

const LABEL: Record<string, string> = {
  cpi: 'CPI',
  d1: 'Retención D1',
  d7: 'Retención D7',
  d30: 'Retención D30',
  ltv_d7: 'LTV D7',
  ltv_d30: 'LTV D30',
  roas_d7: 'ROAS D7',
  roas_d30: 'ROAS D30',
  cpi_ltv_d30: 'CPI / LTV D30',
  payback_days: 'Payback',
  arpdau: 'ARPDAU',
};

/** Columns of the by-country table (the rest: API / CLI). */
const COUNTRY_METRICS = ['cpi', 'd1', 'd7', 'ltv_d7', 'roas_d7', 'roas_d30'];

export function cellValue(c: EconomicsCell, ccy: string): string {
  if (c.value === null) return '—';
  switch (c.unit) {
    case 'rate':
      return fmtPct(c.value);
    case 'ratio':
      return c.metric.startsWith('roas') ? fmtRoas(c.value) : fmtNum(c.value, 2);
    case 'days':
      return `${fmtInt(c.value)} días`;
    default:
      return fmtMoney(c.value, ccy, c.metric === 'arpdau' ? 4 : 3);
  }
}

function targetText(c: EconomicsCell, ccy: string): string {
  if (!c.target) return 'sin objetivo';
  const v = (x: number) => cellValue({ ...c, value: x }, ccy);
  return c.direction === 'higher' ? `verde ≥ ${v(c.target.green)} · rojo < ${v(c.target.red)}` : `verde ≤ ${v(c.target.green)} · rojo > ${v(c.target.red)}`;
}

function MetricCard({ c, ccy }: { c: EconomicsCell; ccy: string }) {
  const notReached = c.lower_bound !== undefined;
  return (
    <div className={`tile econ ${c.status}`}>
      <div className="tile-head">
        <span className="tile-label">{LABEL[c.metric] ?? c.label}</span>
        <TargetBadge status={c.status} uncertain={c.uncertain} />
      </div>
      <div className="tile-main">
        <span className={`tile-value${notReached ? ' text' : ''}`}>{notReached ? `> ${fmtInt(c.lower_bound)} días` : cellValue(c, ccy)}</span>
      </div>
      <div className="tile-foot tile-sub">
        {targetText(c, ccy)}
        {c.sample !== null ? ` · n=${fmtInt(c.sample)}` : ''}
        {c.ci && c.ci.low !== null ? ` · IC 95 % ${cellValue({ ...c, value: c.ci.low }, ccy)}–${cellValue({ ...c, value: c.ci.high }, ccy)}` : ''}
      </div>
    </div>
  );
}

export function EconomicsPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const money = useMoneyParams();
  const q = { ...filters.params, basis: money.basis, attribution: money.attribution };
  const r = useApi<UnitEconomics>(`eco:${gameId}:${filters.key}:${money.basis}:${money.attribution}`, (api, s) => api.economics(gameId, q, s));
  const byCountry = useApi<UnitEconomicsByCountry>(`eco-c:${gameId}:${filters.key}:${money.basis}:${money.attribution}`, (api, s) => api.economicsByCountry(gameId, q, s));
  const d = r.data;
  const ccy = d?.currency ?? 'EUR';
  const countryMetrics = (byCountry.data?.rows[0]?.cells.map((c) => c.metric) ?? []).filter((m) => COUNTRY_METRICS.includes(m));

  return (
    <>
      <PageHeader
        title="Unit economics"
        sub={d ? `${fmtInt(d.installs)} jugadores · gasto ${fmtMoney(d.spend, ccy)} · atribución ${d.attribution === 'shared' ? 'compartida' : 'mezclada'}` : 'CPI, retención, LTV, ROAS y payback frente a los objetivos del juego'}
        right={
          <>
            <AttributionToggle value={money.attribution} onChange={money.setAttribution} />
            <BasisToggle value={money.basis} onChange={money.setBasis} />
          </>
        }
      />
      <FilterBar filters={filters} compare={false} environment={false} />
      {r.error && <ErrorBox error={r.error} onRetry={r.reload} />}
      {d && d.targets_set === 0 && (
        <div className="notice">
          Este juego no tiene objetivos: las métricas se muestran sin color. No hay umbrales por defecto: defínelos en{' '}
          <Link to={`/g/${gameId}/settings/targets`}>Configuración ▸ Objetivos</Link>.
        </div>
      )}
      <div className={`kpis loading-frame${r.loading && d ? ' stale' : ''}`}>
        {(d?.cells ?? []).map((c) => (
          <MetricCard key={c.metric} c={c} ccy={ccy} />
        ))}
      </div>
      {d?.estimated_payback && (
        <p className="small muted">
          Payback ESTIMADO: {d.estimated_payback.day === null ? 'más allá de 365 días' : `~día ${d.estimated_payback.day}`} (curva potencial, R² {d.estimated_payback.r2}). Es una proyección, no un dato
          observado.
        </p>
      )}

      <Card title="Por país" subtitle="CPI del informe de ubicación de Google Ads; retención, LTV y ROAS de los jugadores instalados en cada país.">
        {byCountry.error && <ErrorBox error={byCountry.error} onRetry={byCountry.reload} />}
        {byCountry.data && byCountry.data.rows.length === 0 ? (
          <Empty>Sin datos por país en el periodo.</Empty>
        ) : (
          <div className={`loading-frame${byCountry.loading && byCountry.data ? ' stale' : ''}`}>
            <DataTable
              data={{
                columns: [{ label: 'País' }, { label: 'Gasto', numeric: true }, { label: 'Jugadores', numeric: true }, ...countryMetrics.map((m) => ({ label: LABEL[m] ?? m, numeric: true }))],
                rows: (byCountry.data?.rows ?? []).map((row) => [
                  row.country,
                  fmtMoney(row.spend, ccy),
                  fmtInt(row.installs),
                  ...row.cells.filter((c) => COUNTRY_METRICS.includes(c.metric)).map((c) => (
                    <span className="row" style={{ justifyContent: 'flex-end', gap: 4, flexWrap: 'nowrap' }}>
                      {cellValue(c, ccy)}
                      {c.status === 'green' || c.status === 'yellow' || c.status === 'red' ? <TargetBadge status={c.status} uncertain={c.uncertain} /> : null}
                    </span>
                  )),
                ]),
              }}
            />
          </div>
        )}
      </Card>
      {d?.notes.map((n) => (
        <p key={n} className="small muted">
          {n}
        </p>
      ))}
      <AssumptionsNote a={d?.assumptions} />
    </>
  );
}
